import {
  ArrowRightIcon,
  ChevronRightIcon,
  PlusIcon,
} from "@heroicons/react/20/solid";
import { H2 } from "../../components/Heading";
import {
  DealRoleType,
  DealStatus,
  HomeQuery,
  useDealDashboardDetailsQuery,
  useHomeQuery,
  useUpdateDealMutation,
} from "../../graphql/generated";
import useGqlClient from "../../hooks/useGqlClient";
import { useHistory } from "react-router-dom";
import CompanyLogo from "../../components/CompanyLogo";
import { formatDistanceToNowStrict, fromUnixTime } from "date-fns";
import { useDispatch, useSelector } from "react-redux";
import { actions } from "../../store/auth/slice";
import Skeleton from "react-loading-skeleton";
import "react-loading-skeleton/dist/skeleton.css";
import { authSelectors } from "../../store/auth/selector";
import { Avatar } from "../../components/account/Avatar";
import { AppState } from "../../store";
import { createContext, useContext, useState } from "react";
import { AddPeopleFromFirmModal } from "../access/AddPeopleFromFirm";
import { useQueryClient } from "@tanstack/react-query";
import { TripleDotMenu } from "../../components/TripleDotMenu";
import { Menu } from "@headlessui/react";
import { classNames } from "../../utils/cn";
import {
  ArchiveBoxArrowDownIcon,
  ArchiveBoxIcon,
} from "@heroicons/react/24/outline";
import { toasts } from "../../components/toasts/toasts";
import { useSession } from "@clerk/clerk-react";
import { SignupSource } from "../Signup";
import {
  CirclePlayIcon,
  PackageOpenIcon,
  SearchIcon,
  SquareArrowDownIcon,
} from "lucide-react";
import { PermissionCheck } from "@/src/components/permissions/PermissionCheck";
import { Toggle } from "@/src/components/Toggle";
import { Button } from "@/src/components/tailwind/Button";
import { CreateDealModal } from "@/src/components/navigation/CreateDealModal";

type TabType = "live" | "archived";

interface HomeContextType {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
  selectedTab: TabType;
  setSelectedTab: (tab: TabType) => void;
  openCreateDealModal: () => void;
}

const HomeContext = createContext<HomeContextType>({
  searchTerm: "",
  setSearchTerm: () => { },
  selectedTab: "live",
  setSelectedTab: () => { },
  openCreateDealModal: () => { },
});

function useHomeContext() {
  return useContext(HomeContext);
}

export function Home() {
  const [selectedTab, setSelectedTab] = useState<TabType>("live");
  const [searchTerm, setSearchTerm] = useState("");
  const [showCreateDealModal, setShowCreateDealModal] = useState(false);

  const openCreateDealModal = () => setShowCreateDealModal(true);

  return (
    <HomeContext.Provider
      value={{
        searchTerm,
        setSearchTerm,
        selectedTab,
        setSelectedTab,
        openCreateDealModal,
      }}
    >
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden">
        <div className="flex min-h-0 flex-1 flex-col p-8">
          <div className="flex shrink-0 items-center gap-x-6">
            <div>
              <H2>Deals</H2>
              <p className="text-sm text-gray-500 leading-none"></p>
            </div>
            <Toggle
              onChange={(o) => {
                setSelectedTab(o as TabType);
              }}
              value={selectedTab}
              options={[
                {
                  id: "live",
                  label: "Live",
                  icon: CirclePlayIcon,
                  iconColor:
                    selectedTab === "live"
                      ? "text-persian-300"
                      : "text-persian-800",
                },
                {
                  id: "archived",
                  label: "Archived",
                  icon: SquareArrowDownIcon,
                  iconColor:
                    selectedTab === "archived"
                      ? "text-persian-300"
                      : "text-persian-800",
                },
              ]}
            ></Toggle>
          </div>
          <div className="mt-4 flex min-h-0 flex-1 flex-col overflow-hidden rounded-lg bg-white shadow">
            <HomeHeader />
            <HomeContent />
          </div>
        </div>
      </div>
      <CreateDealModal
        open={showCreateDealModal}
        onClose={() => setShowCreateDealModal(false)}
      />
    </HomeContext.Provider>
  );
}

function HomeHeader() {
  const { searchTerm, setSearchTerm, openCreateDealModal } = useHomeContext();
  const account = useSelector(authSelectors.account);

  return (
    <div className="p-4 flex items-center justify-between border-b border-concrete-100">
      <div className="relative w-60 ">
        <span className="absolute inset-y-0 left-0 flex items-center pl-3">
          <SearchIcon className="h-4 w-4 text-concrete-300" />
        </span>
        <input
          type="text"
          placeholder="Search deals..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="placeholder:text-concrete-400 block w-full pl-10 pr-3 py-1 border border-concrete-100 rounded-md focus:outline-none focus:ring-1 focus:ring-concrete-500 focus:border-concrete-700 sm:text-sm"
        />
      </div>

      <div>
        {account && account.firm ? (
          <Button
            variant="positive"
            icon={PlusIcon}
            text="Create new deal"
            onClick={openCreateDealModal}
          />
        ) : null}
      </div>
    </div>
  );
}

function HomeContent() {
  const client = useGqlClient();
  const homeQuery = useHomeQuery(client);
  const { session } = useSession();
  const { searchTerm, selectedTab } = useHomeContext();

  if (homeQuery.error) {
    return (
      <div>
        <p className="text-gray-700 font-bold">Something went wrong</p>
      </div>
    );
  }

  if (homeQuery.isLoading || !homeQuery.data) {
    return (
      <div className="grid min-h-0 flex-1 grid-cols-1 content-start gap-4 overflow-y-auto p-4 lg:grid-cols-2 xl:grid-cols-2">
        {[0, 1, 2, 3, 4, 5, 6].map(() => {
          return (
            <div className="animate-pulse border border-concrete-100 p-4 rounded-md">
              <div className="flex items-center gap-x-2">
                <div className="w-10 h-10 rounded-full bg-concrete-200"></div>
                <div className="w-40 h-6 rounded-md bg-concrete-200"></div>
              </div>
              <div className="mt-6 gap-x-6  grid  grid-cols-2 lg:grid-cols-4 gap-y-4">
                {[0, 1, 2].map(() => {
                  return (
                    <div>
                      <div className="w-16 h-4 bg-concrete-200"></div>
                      <div className="w-20 h-8 mt-2 bg-concrete-200"></div>
                    </div>
                  );
                })}
              </div>
              <div className="mt-4">
                <div className="w-16 h-4 bg-concrete-200"></div>
                <div className="flex mt-2 items-center -space-x-1.5">
                  {[0, 1, 2].map(() => {
                    return (
                      <div className="w-4 h-4 rounded-full bg-concrete-200"></div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  const signUpSource =
    (session?.user.unsafeMetadata.signupSource as SignupSource) ??
    SignupSource.signupPage;

  const deals = [...homeQuery.data.account.deals];

  const targetStatus =
    selectedTab === "live" ? DealStatus.Active : DealStatus.Archived;

  const filteredDeals = deals
    .sort((a, b) => {
      return a.createdAt > b.createdAt ? -1 : 1;
    })
    .filter((d) => d.status === targetStatus)
    .filter((d) => {
      if (!searchTerm.trim()) return true;
      const search = searchTerm.toLowerCase();
      return d.company.name.toLowerCase().includes(search);
    });

  const emptyMessage = searchTerm.trim()
    ? "No matching deals"
    : selectedTab === "live"
      ? "No live deals"
      : "No archived deals";

  return (
    <div className="min-h-0 flex-1 overflow-y-auto">
      <SignupBanner signupSource={signUpSource} />
      <div>
        {filteredDeals.length === 0 ? (
          <div className="mt-8">
            <div className="flex items-center justify-center flex-col">
              <PackageOpenIcon className="h-8 w-8 text-gray-400" />
              <p className="font-semibold text-gray-600">{emptyMessage}</p>
            </div>
          </div>
        ) : null}
        <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-2 gap-4 p-4">
          {filteredDeals.map((deal) => {
            return (
              <div key={deal.id} className="cursor-pointer text-left">
                <DealCard deal={deal} />
              </div>
            );
          })}
        </div>
      </div>
    </div>
  );
}

function SignupBanner(props: { signupSource: SignupSource }) {
  if (props.signupSource !== SignupSource.externalInvite) {
    return null;
  }

  return (
    <div className="flex items-center justify-between w-full p-3 rounded-md bg-gradient-to-br from-persian-950/90 to-persian-950/80 mb-6 border-2 shadow-sm border-persian-200">
      <div>
        <p className="text-md text-gray-50 font-semibold">Sign up to Liquid</p>
        <p className="text-sm text-gray-50">
          Find out why more and more firms are switching to Liquid to run their
          deals
        </p>
      </div>
      <a
        href={`mailto:hello@liquidacquire.com?subject=${encodeURIComponent(
          "I'm interested in Liquid",
        )}`}
        target="_blank"
        rel="noreferrer"
      >
        <p className="text-white font-semibold text-sm  group flex items-center">
          Talk to us
          <ArrowRightIcon className="text-white w-4 h-4 group-hover:ml-2 ml-0.5 transition-all" />
        </p>
      </a>
    </div>
  );
}

export function DealCard(props: {
  deal: HomeQuery["account"]["deals"][0];
  truncate?: boolean;
  activeLink?: boolean;
}) {
  const activeLink = props.activeLink ?? true;
  const truncate = props.truncate ?? false;
  const deal = props.deal;
  const dealLastSeen = useSelector((state: AppState) =>
    authSelectors.dealLastSeen(state, deal.id),
  );

  const lastSeen = dealLastSeen ? dealLastSeen : deal.createdAt;

  const client = useGqlClient();
  const queryClient = useQueryClient();
  const updateDeal = useUpdateDealMutation(client);
  const history = useHistory();
  const dispatch = useDispatch();

  return (
    <div className="rounded-md border border-concrete-100 p-4 " key={deal.id}>
      <div
        className="flex items-start justify-between"
        onClick={() => {
          if (activeLink) {
            dispatch(actions.setActiveDealId({ dealId: deal.id }));
            history.push(`/deal/${deal.id}/dashboard`);
          }
        }}
      >
        <div className="flex items-center">
          <CompanyLogo
            logo={deal.company.logo}
            name={deal.company.name}
            bgColor={deal.company.logoColor}
            withShadow={false}
          />
          <div className="ml-2">
            <div>
              <p className="font-semibold leading-tight text-persian-990 text-md">
                {deal.company.name}
              </p>
            </div>
          </div>
        </div>
        {truncate ? null : (
          <PermissionCheck
            input={{
              permissionKey: "deal:edit",
              roleType: DealRoleType.Internal,
              dealID: deal.id,
            }}
          >
            <TripleDotMenu width="w-40">
              <Menu.Items>
                <Menu.Item>
                  {({ active }) => (
                    <div
                      onClick={(e) => {
                        e.stopPropagation();
                        const updateStatus =
                          deal.status === DealStatus.Active
                            ? DealStatus.Archived
                            : DealStatus.Active;

                        updateDeal.mutate(
                          {
                            input: {
                              id: deal.id,
                              status: updateStatus,
                            },
                          },
                          {
                            onError: () => {
                              toasts.error(
                                `${updateStatus === DealStatus.Active
                                  ? "Unarchive"
                                  : "Archive"
                                } deal failed`,
                              );
                            },
                            onSuccess: () => {
                              toasts.info(
                                `${updateStatus === DealStatus.Active
                                  ? "Deal unarchived"
                                  : "Deal archived"
                                }`,
                              );
                              queryClient.invalidateQueries({
                                queryKey: ["Account"],
                              });
                              queryClient.invalidateQueries({
                                queryKey: ["Home"],
                              });
                            },
                          },
                        );
                      }}
                      className={classNames(
                        active ? "bg-gray-50 text-orange-800" : "",
                        "font-semibold block px-3 py-1 text-sm leading-6 text-orange-700/90 cursor-pointer",
                      )}
                    >
                      {deal.status === DealStatus.Archived ? (
                        <>
                          <ArchiveBoxArrowDownIcon className="w-4 h-4 mr-2  inline-block" />
                          Unarchive deal
                        </>
                      ) : (
                        <>
                          <ArchiveBoxIcon className="w-4 h-4 mr-2  inline-block" />
                          Archive deal
                        </>
                      )}
                    </div>
                  )}
                </Menu.Item>
              </Menu.Items>
            </TripleDotMenu>
          </PermissionCheck>
        )}
      </div>
      {truncate ? null : <DealDetails id={deal.id} />}
    </div>
  );
}

function DealDetails(props: { id: string }) {
  const client = useGqlClient();

  const account = useSelector(authSelectors.account);
  const dealLastSeen = useSelector((state: AppState) =>
    authSelectors.dealLastSeen(state, props.id),
  );

  const [openModal, setOpenModal] = useState<"add_people" | "">("");

  const history = useHistory();
  const queryClient = useQueryClient();

  const dealDetailsQuery = useDealDashboardDetailsQuery(client, {
    dealId: props.id,
  });

  if (dealDetailsQuery.error) {
    return (
      <div>
        <p className="font-semibold text-gray-500">Something went wrong</p>
      </div>
    );
  }

  if (dealDetailsQuery.isPending || !dealDetailsQuery.data || !account) {
    return (
      <div className="mt-2 flex gap-x-4">
        <div className=" flex-1">
          <Skeleton style={{ width: "30%" }} />
          <Skeleton height={30} style={{ width: "100%" }} />
        </div>
        <div className=" flex-1">
          <Skeleton style={{ width: "30%" }} />
          <Skeleton height={30} style={{ width: "100%" }} />
        </div>
      </div>
    );
  }

  return (
    <div className="mt-4 gap-x-6 flex items-start flex-col ">
      <div className="flex flex-col w-full">
        <div className="flex-1 ">
          <div className="grid mt-2 grid-cols-2 lg:grid-cols-4 gap-y-4">
            <button
              className="text-left group"
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/deal/${props.id}/documents`);
              }}
            >
              <div className="flex items-center">
                <p className="text-xs text-concrete-600 font-semibold truncate">
                  Documents
                </p>
                <ChevronRightIcon className="w-4 text-gray-500 h-4 hidden group-hover:block" />
              </div>
              <p className="font-semibold text-lg text-concrete-950">
                {dealDetailsQuery.data.deal.totalFiles}
              </p>
            </button>

            <button
              className="text-left group"
              onClick={(e) => {
                e.stopPropagation();
                history.push(`/deal/${props.id}/questions`);
              }}
            >
              <div className="flex items-center">
                <p className="text-xs text-concrete-600 font-semibold truncate">
                  Questions
                </p>
                <ChevronRightIcon className="w-4 text-gray-500 h-4 hidden group-hover:block" />
              </div>
              <p className="font-semibold text-lg text-concrete-950">
                {dealDetailsQuery.data.deal.totalAnsweredQuestions} /{" "}
                {dealDetailsQuery.data.deal.totalQuestions}
              </p>
            </button>

            <div className="text-left group">
              <div className="flex items-center">
                <p className="text-xs text-concrete-600 font-semibold truncate">
                  Deal Duration
                </p>
              </div>
              <p className="font-semibold text-lg text-concrete-950">
                {formatDistanceToNowStrict(
                  fromUnixTime(dealDetailsQuery.data.deal.createdAt),
                )}
              </p>
            </div>
          </div>
        </div>
        <PermissionCheck
          input={{
            permissionKey: "access:has_access",
            roleType: DealRoleType.Internal,
            dealID: props.id,
          }}
        >
          <div className="flex-1 mt-4">
            <p className="text-xs font-semibold text-concrete-600">My firm</p>
            <div className="flex mt-2 transition-all hover:space-x-0.5 -space-x-0.5">
              {dealDetailsQuery.data.deal.dealAccounts
                .filter((da) => {
                  if (!account.firm || !da.account.firm) {
                    return false;
                  }

                  return da.account.firm.id === account.firm.id;
                })
                .map((da) => {
                  return <Avatar account={da.account} />;
                })}
              <button
                onClick={(e) => {
                  e.stopPropagation();
                  setOpenModal("add_people");
                }}
                className="w-6 h-6 z-20 bg-gray-100 hover:bg-gray-200 rounded-full flex items-center justify-center"
              >
                <PlusIcon className="w-4 h-4 text-gray-500" />
              </button>
            </div>
          </div>
        </PermissionCheck>
      </div>

      <AddPeopleFromFirmModal
        dealId={props.id}
        open={openModal === "add_people"}
        onClose={() => {
          queryClient.invalidateQueries({
            queryKey: ["DealDashboardDetails", { dealId: props.id }],
          });
          setOpenModal("");
        }}
      />
    </div>
  );
}
